import { useMemo, useState, useRef, useEffect } from "react";
import { WeekDayStripStyles } from "./weekDayStrip.styles";

const WEEKDAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec"
];

const stripTime = date => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const addDays = (date, amount) => {
  const d = new Date(date);
  d.setDate(d.getDate() + amount);
  return d;
};

const getWeekStart = date => {
  const d = stripTime(date);
  const diff = d.getDay(); // 0 = Sunday → start on Sunday
  return addDays(d, -diff);
};

const isSameDay = (a, b) => {
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
};

const ChevronLeft = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M10 3L5 8l5 5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronRight = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M6 3l5 5-5 5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const WeekDayStrip = ({ value, onChange, minDate }) => {
  const today = useMemo(() => stripTime(new Date()), []);
  const [anchor, setAnchor] = useState(() =>
    getWeekStart(value || today)
  );
  const stripRef = useRef(null);

  useEffect(() => {
    if (value) {
      const weekStart = getWeekStart(value);
      if (weekStart.getTime() !== anchor.getTime()) {
        setAnchor(weekStart);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const days = useMemo(() => {
    return Array.from({ length: 7 }).map((_, i) => addDays(anchor, i));
  }, [anchor]);

  const shiftWeek = delta => {
    setAnchor(prev => addDays(prev, delta * 7));
  };

  const monthLabel = useMemo(() => {
    const first = days[0];
    const last = days[6];
    if (first.getMonth() === last.getMonth()) {
      return `${MONTH_NAMES[first.getMonth()]} ${first.getFullYear()}`;
    }
    return `${MONTH_NAMES[first.getMonth()]} – ${MONTH_NAMES[last.getMonth()]} ${last.getFullYear()}`;
  }, [days]);

  const handleWheel = e => {
    if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
    if (e.deltaY > 0) shiftWeek(1);
    else if (e.deltaY < 0) shiftWeek(-1);
  };

  const isDisabled = day => minDate && day < stripTime(minDate);

  return (
    <WeekDayStripStyles>
      <div className="header">
        <button
          type="button"
          className="navButton"
          onClick={() => shiftWeek(-1)}
          aria-label="Previous week"
        >
          <ChevronLeft />
        </button>
        <span className="monthLabel">{monthLabel}</span>
        <button
          type="button"
          className="navButton"
          onClick={() => shiftWeek(1)}
          aria-label="Next week"
        >
          <ChevronRight />
        </button>
      </div>
      <div className="strip" ref={stripRef} onWheel={handleWheel}>
        {days.map(day => {
          const selected = isSameDay(day, value);
          const isToday = isSameDay(day, today);
          const disabled = isDisabled(day);
          const cls = [
            "dayCell",
            selected ? "selected" : "",
            isToday ? "today" : "",
            disabled ? "disabled" : ""
          ]
            .filter(Boolean)
            .join(" ");
          return (
            <button
              key={day.toISOString()}
              type="button"
              className={cls}
              disabled={disabled}
              onClick={() => onChange(day)}
            >
              <span className="weekday">
                {WEEKDAY_NAMES[day.getDay()]}
              </span>
              <span className="dayNumber">{day.getDate()}</span>
              <span className="monthMini">
                {MONTH_NAMES[day.getMonth()]}
              </span>
            </button>
          );
        })}
      </div>
    </WeekDayStripStyles>
  );
};

export default WeekDayStrip;
